import { useEffect, useState } from 'react';
import { Routes, Route, Navigate, Outlet, useLocation } from 'react-router-dom';
import { get_current_admin, type AdminProfile } from './auth';
import Login from './pages/Login';
import Home from './pages/Home';
import Tenants from './pages/Tenants';
import TenantDetail from './pages/TenantDetail';
import UserDetail from './pages/UserDetail';
import Seed from './pages/Seed';
import Apps from './pages/Apps';
import Bookings from './pages/Bookings';
import Usage from './pages/Usage';
import Business360 from './pages/Business360';
import Business360Dataset from './pages/Business360Dataset';
import AppDetail from './pages/AppDetail';
import Admins from './pages/Admins';
import Audit from './pages/Audit';
import Nav from './components/Nav';
import ErrorBoundary from './components/ErrorBoundary';

function AppShell({
  admin,
  on_signed_out,
}: {
  admin: AdminProfile;
  on_signed_out: () => void;
}) {
  const location = useLocation();
  return (
    <>
      <Nav admin={admin} on_signed_out={on_signed_out} />
      {/* Keyed by route so a crash on one page resets when you navigate away. */}
      <ErrorBoundary key={location.pathname}>
        <Outlet />
      </ErrorBoundary>
    </>
  );
}

export default function App() {
  const [admin, set_admin] = useState<AdminProfile | null>(null);
  const [loading, set_loading] = useState(true);
  const location = useLocation();

  useEffect(() => {
    get_current_admin()
      .then(set_admin)
      .catch(() => set_admin(null))
      .finally(() => set_loading(false));
  }, []);

  if (loading) {
    return <div className="container">Loading...</div>;
  }

  return (
    <Routes>
      <Route
        path="/login"
        element={
          admin ? <Navigate to="/" replace /> : <Login on_signed_in={set_admin} />
        }
      />
      {admin ? (
        <Route
          element={<AppShell admin={admin} on_signed_out={() => set_admin(null)} />}
        >
          <Route path="/" element={<Home admin={admin} />} />
          <Route path="/tenants" element={<Tenants />} />
          <Route path="/tenants/:tenant_id" element={<TenantDetail />} />
          <Route
            path="/tenants/:tenant_id/users/:user_id"
            element={<UserDetail />}
          />
          <Route path="/bookings" element={<Bookings />} />
          <Route path="/usage" element={<Usage />} />
          <Route path="/business360" element={<Business360 />} />
          <Route path="/business360/:dataset_id" element={<Business360Dataset />} />
          <Route path="/seed" element={<Seed />} />
          <Route path="/apps" element={<Apps />} />
          <Route path="/apps/:app_code" element={<AppDetail />} />
          <Route path="/admins" element={<Admins admin={admin} />} />
          <Route path="/audit" element={<Audit />} />
          <Route path="*" element={<Navigate to="/" replace />} />
        </Route>
      ) : (
        <Route
          path="*"
          element={
            <Navigate
              to={`/login?next=${encodeURIComponent(location.pathname)}`}
              replace
            />
          }
        />
      )}
    </Routes>
  );
}
